import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { ROEFormPayload, roeFormState } from '@/state/project';

const CUSTOM_PRESET_LABEL = '— Custom —';

type FieldKey =
  | 'allowed_techniques'
  | 'denied_techniques'
  | 'allowed_tools'
  | 'denied_tools'
  | 'allowed_target_ips';

type FormState = {
  preset: string;
  allowed_techniques: string[];
  denied_techniques: string[];
  allowed_tools: string[];
  denied_tools: string[];
  notes: string;
  allowed_target_ips: string[];
};

function blankForm(): FormState {
  return {
    preset: CUSTOM_PRESET_LABEL,
    allowed_techniques: [],
    denied_techniques: [],
    allowed_tools: [],
    denied_tools: [],
    notes: '',
    allowed_target_ips: []
  };
}

function buildUntouchedNotes(payload: ROEFormPayload | null): Set<string> {
  const set = new Set<string>(['']);
  if (!payload) return set;
  Object.values(payload.presets || {}).forEach((preset) => {
    const note = (preset as { notes?: string }).notes;
    if (typeof note === 'string') set.add(note);
  });
  if (payload.default && typeof payload.default.notes === 'string') {
    set.add(payload.default.notes);
  }
  return set;
}

function presetField(
  preset: Record<string, string | string[] | undefined> | undefined,
  key: FieldKey
): string[] {
  if (!preset) return [];
  const value = preset[key];
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function presetNotes(
  preset: Record<string, string | string[] | undefined> | undefined
): string {
  if (!preset) return '';
  const note = preset.notes;
  return typeof note === 'string' ? note : '';
}

function ChipInput({
  id,
  label,
  values,
  onChange,
  placeholder,
  knownTokens,
  caseTransform
}: {
  id: string;
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  knownTokens: Set<string>;
  caseTransform: 'upper' | 'lower' | 'none';
}) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const tokens = raw
      .split(',')
      .map((tok) => tok.trim())
      .filter(Boolean);
    if (!tokens.length) return;
    const transformed = tokens.map((tok) =>
      caseTransform === 'upper'
        ? tok.toUpperCase()
        : caseTransform === 'lower'
        ? tok.toLowerCase()
        : tok
    );
    const next = [...values];
    for (const tok of transformed) {
      if (!next.includes(tok)) next.push(tok);
    }
    onChange(next);
    setDraft('');
  };

  const remove = (index: number) => {
    const next = values.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-2 min-h-[40px]">
        {values.map((value, index) => {
          const lookup =
            caseTransform === 'upper'
              ? value.toUpperCase()
              : value.toLowerCase();
          const known = knownTokens.size === 0 ? true : knownTokens.has(lookup);
          const chip = (
            <Badge
              key={`${value}-${index}`}
              variant={known ? 'secondary' : 'outline'}
              className={cn(
                'gap-1 pl-2 pr-1 py-0.5 text-xs font-medium',
                !known &&
                  'border-yellow-500 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'
              )}
            >
              {value}
              <button
                type="button"
                onClick={() => remove(index)}
                className="ml-0.5 rounded-sm p-0.5 hover:bg-muted"
                aria-label={`Remove ${value}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
          if (known) return chip;
          return (
            <TooltipProvider key={`${value}-${index}-tip`} delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>{chip}</TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[260px] text-xs">
                    Unknown {label.toLowerCase()}: <code>{value}</code>. Will be
                    dropped on submit.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
        <Input
          id={id}
          className="border-0 shadow-none focus-visible:ring-0 flex-1 min-w-[120px] h-7 px-1 py-0 text-sm"
          placeholder={values.length ? '' : placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            } else if (
              e.key === 'Backspace' &&
              draft.length === 0 &&
              values.length > 0
            ) {
              remove(values.length - 1);
            }
          }}
          onBlur={() => commit(draft)}
        />
      </div>
    </div>
  );
}

export default function ROEFormDialog() {
  const [payload, setPayload] = useRecoilState(roeFormState);
  const session = useRecoilValue(sessionState);
  const [form, setForm] = useState<FormState>(blankForm());

  const untouchedNotes = useMemo(() => buildUntouchedNotes(payload), [payload]);

  useEffect(() => {
    if (payload) {
      setForm({
        preset: CUSTOM_PRESET_LABEL,
        allowed_techniques: payload.current.allowed_techniques.slice(),
        denied_techniques: payload.current.denied_techniques.slice(),
        allowed_tools: payload.current.allowed_tools.slice(),
        denied_tools: payload.current.denied_tools.slice(),
        notes: payload.current.notes || '',
        allowed_target_ips: (payload.current.allowed_target_ips || []).slice()
      });
    } else {
      setForm(blankForm());
    }
  }, [payload]);

  if (!payload) return null;

  const knownTechniques = new Set(
    (payload.known_techniques || []).map((t) => t.toUpperCase())
  );
  const knownTools = new Set(
    (payload.known_tools || []).map((t) => t.toLowerCase())
  );

  const isEditMode = payload.mode === 'edit';

  const applyPreset = (presetKey: string) => {
    if (presetKey === CUSTOM_PRESET_LABEL) {
      setForm((prev) => ({ ...prev, preset: presetKey }));
      return;
    }
    const preset = payload.presets[presetKey];
    if (!preset) return;
    setForm((prev) => {
      const notesAreOperatorOwned = !untouchedNotes.has(prev.notes);
      return {
        ...prev,
        preset: presetKey,
        allowed_techniques: presetField(preset, 'allowed_techniques'),
        denied_techniques: presetField(preset, 'denied_techniques'),
        allowed_tools: presetField(preset, 'allowed_tools'),
        denied_tools: presetField(preset, 'denied_tools'),
        allowed_target_ips: prev.allowed_target_ips,
        notes: notesAreOperatorOwned ? prev.notes : presetNotes(preset)
      };
    });
  };

  const loadDefault = () => {
    setForm((prev) => {
      const notesAreOperatorOwned = !untouchedNotes.has(prev.notes);
      return {
        ...prev,
        preset: CUSTOM_PRESET_LABEL,
        allowed_techniques: payload.default.allowed_techniques.slice(),
        denied_techniques: payload.default.denied_techniques.slice(),
        allowed_tools: payload.default.allowed_tools.slice(),
        denied_tools: payload.default.denied_tools.slice(),
        allowed_target_ips: (payload.default.allowed_target_ips || []).slice(),
        notes: notesAreOperatorOwned ? prev.notes : payload.default.notes || ''
      };
    });
  };

  const emit = (action: 'submit' | 'skip' | 'cancel') => {
    const socket = session?.socket;
    if (socket && typeof socket.emit === 'function') {
      socket.emit('roe_form_response', {
        form_id: payload.form_id,
        action,
        allowed_techniques: form.allowed_techniques,
        denied_techniques: form.denied_techniques,
        allowed_tools: form.allowed_tools,
        denied_tools: form.denied_tools,
        notes: form.notes,
        allowed_target_ips: form.allowed_target_ips
      });
    }
    setPayload(null);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && emit('cancel')}>
      <DialogContent className="w-[92vw] max-w-[820px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rules of Engagement Setup</DialogTitle>
          <DialogDescription>
            Define which ATT&amp;CK techniques and Kali tools are in scope for
            this engagement. Pick a preset, customise the fields, or load the
            permissive default.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="roe-preset">Apply preset</Label>
            <div className="flex gap-2">
              <Select value={form.preset} onValueChange={applyPreset}>
                <SelectTrigger id="roe-preset" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_PRESET_LABEL}>
                    {CUSTOM_PRESET_LABEL}
                  </SelectItem>
                  {Object.keys(payload.presets || {}).map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={loadDefault}>
                Load default
              </Button>
            </div>
          </div>

          <ChipInput
            id="roe-allowed-techniques"
            label="Authorized Techniques"
            values={form.allowed_techniques}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                allowed_techniques: next,
                preset: CUSTOM_PRESET_LABEL
              }))
            }
            placeholder="T1190, T1059, T1046"
            knownTokens={knownTechniques}
            caseTransform="upper"
          />
          <ChipInput
            id="roe-denied-techniques"
            label="Prohibited Techniques"
            values={form.denied_techniques}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                denied_techniques: next,
                preset: CUSTOM_PRESET_LABEL
              }))
            }
            placeholder="T1499, T1485"
            knownTokens={knownTechniques}
            caseTransform="upper"
          />
          <ChipInput
            id="roe-allowed-tools"
            label="Authorized Tools"
            values={form.allowed_tools}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                allowed_tools: next,
                preset: CUSTOM_PRESET_LABEL
              }))
            }
            placeholder="nmap, metasploit, hydra"
            knownTokens={knownTools}
            caseTransform="lower"
          />
          <ChipInput
            id="roe-denied-tools"
            label="Prohibited Tools"
            values={form.denied_tools}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                denied_tools: next,
                preset: CUSTOM_PRESET_LABEL
              }))
            }
            placeholder="mimikatz"
            knownTokens={knownTools}
            caseTransform="lower"
          />
          <ChipInput
            id="roe-allowed-target-ips"
            label="Allowed Target IPs"
            values={form.allowed_target_ips}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                allowed_target_ips: next,
                preset: CUSTOM_PRESET_LABEL
              }))
            }
            placeholder="10.0.0.0/24, 10.0.0.5, 10.0.0.5-10.0.0.20"
            knownTokens={new Set<string>()}
            caseTransform="none"
          />

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="roe-notes">Scope Notes</Label>
            <Textarea
              id="roe-notes"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="No real-world exfil. Test window: 9am-5pm CT."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex sm:justify-end gap-2">
          {isEditMode ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => emit('cancel')}
            >
              Cancel
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => emit('skip')}
            >
              Skip
            </Button>
          )}
          <Button type="button" onClick={() => emit('submit')}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
